import React, { useMemo } from "react";
import {
  Empty, Modal, Table,
} from "antd";

import "./QcBulkUploadResultModal.css";

const EMPTY_VALUE = "-";

const toDisplayValue = (value) => {
  if (value == null || value === "") return EMPTY_VALUE;
  return String(value);
};

const getRowPayload = (item) => {
  const payload = item?.row ?? item?.data ?? item?.payload ?? item;
  return payload && typeof payload === "object" ? payload : {};
};

const normalizeResultRows = (result) => {
  if (!Array.isArray(result)) {
    return [];
  }

  return result.map((item, index) => ({
    key: item._id ?? item.rule_id ?? `result-${index}`,
    ...getRowPayload(item),
  }));
};

const getSampleInfo = (record, type) => {
  if (type === "ranges") {
    return toDisplayValue(record.sample_info);
  }
  // sample_info for rules
  if (typeof record.sample_info === "string") {
    return toDisplayValue(record.sample_info);
  }
  if (!record.sample_info || typeof record.sample_info !== "object") {
    return null;
  }
  if (record.sample_info.sample_type) {
    return toDisplayValue(record.sample_info.sample_type);
  }
  const keys = Object.keys(record.sample_info);
  if (keys.length === 1) {
    return toDisplayValue(keys[0]);
  }
  return null;
};

const getRuleSpecs = (record) => {
  if (!record.sample_info || typeof record.sample_info !== "object") {
    return toDisplayValue(record.specs);
  }
  if (record.sample_info.specs) {
    return toDisplayValue(record.sample_info.specs);
  }
  const keys = Object.keys(record.sample_info);
  if (keys.length === 1) {
    const info = record.sample_info[keys[0]];
    return toDisplayValue(info?.specs);
  }
  return toDisplayValue(record.specs);
};

const getRangeSpecs = (record) => {
  const rangeSpec = record.range_spec;
  if (!rangeSpec || typeof rangeSpec !== "object") return EMPTY_VALUE;
  if (rangeSpec.raw_value) return rangeSpec.raw_value;

  const minValue = rangeSpec.min_value;
  const maxValue = rangeSpec.max_value;
  const unit = rangeSpec.unit;
  if (!minValue || !maxValue) return EMPTY_VALUE;
  return `${minValue}${unit} - ${maxValue}${unit}`;
};

const getColumns = (type) => {
  const baseColumns = [
    {
      title: "Customer ID",
      dataIndex: "customer_id",
      key: "customer_id",
      width: 150,
      render: toDisplayValue,
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      width: 120,
      render: toDisplayValue,
    },
    {
      title: "Sample Info",
      key: "sample_info",
      width: 180,
      render: (_, record) => getSampleInfo(record, type),
    },
  ];

  const specColumn = type === "ranges"
    ? {
      title: "Range Specs",
      key: "range_specs",
      width: 180,
      render: (_, record) => getRangeSpecs(record),
    }
    : {
      title: "Specs",
      key: "specs",
      width: 180,
      render: (_, record) => getRuleSpecs(record),
    };

  return [
    ...baseColumns,
    specColumn,
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 200,
      render: (_, record) => toDisplayValue(record.message),
    },
  ];
};

export default function QcBulkUploadResultModal({
  open,
  type,
  result,
  onCancel,
}) {
  const dataSource = useMemo(() => normalizeResultRows(result), [result]);
  const columns = useMemo(() => getColumns(type), [type]);

  return (
    <Modal
      centered
      title="Bulk Upload Result"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1000}
      destroyOnClose
      rootClassName="QcBulkUploadResultModal"
    >
      <Table
        bordered
        className="QcBulkUploadResultModal__Table"
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No upload result rows returned." /> }}
        scroll={{ y: 320 }}
      />
    </Modal>
  );
}
